import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { updateTitle } from 'store/app';
import { assetDetails, retrieveAsset } from 'store/asset';
import { getImage } from 'hooks/get-image';
import { Icon } from '@iconify/react';
import Button from 'components/ui/Button';
import cn from 'classnames';
import SwipeButton from 'components/ui/SwipeButton';
import { format } from 'date-fns';

const AssetPickup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const { selected } = useSelector(state => state.asset);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [terms, setTerms] = useState(false);

  const now = new Date();
  const date = format(now, 'dd/MM/yyyy');
  const hour = format(now, 'HH:mm');

  const [dateEstimed, hourEstimed] = selected ? selected?.estimated.split(' ') : ['--', '--'];
  const days = selected?.timer ? Math.floor(selected.timer / 24) : 0;

  const available = [4, 5];

  const items = selected ? [
    {
      icon: 'lucide:calendar-arrow-up',
      title: t('asset.pickup'),
      text: date,
      note: hour
    },
    {
      icon: 'lucide:calendar-arrow-down',
      title: t('asset.deposit'),
      text: dateEstimed,
      note: hourEstimed
    },
    {
      icon: 'lucide:calendar-clock',
      title: t('asset.duration'),
      text: days + ' ' + t('general.day', { count: days }),
      note: selected?.timer + ' ' + t('general.hour', { count: selected?.timer })
    },
    {
      icon: 'lucide:lock',
      title: t('locker.label'),
      text: t('general.number') + ' ' + selected?.locker?.doorNumber,
      note: t('locker.size', { size: selected?.locker?.size })
    }
  ] : [];

  const fetchDetails = () => {
    setLoading(true);
    dispatch(assetDetails(id))
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchDetails();
    dispatch(updateTitle(t('asset.pickup_asset')));
  }, []);

  const handleRetrieve = () => {
    setCompleted(false);
    dispatch(retrieveAsset(selected.id))
      .then((res) => {
        if (res.payload.Result) {
          navigate('/locker/opened');
        }
        setCompleted(true);
        fetchDetails()
      })
      .catch(() => {
        setCompleted(true);
        fetchDetails();
      })
  };

  if (loading || !selected) {
    return (
      <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6 animate-pulse">
        <div className="h-6 bg-gray-300 rounded w-1/3" />
        <div className="h-5 bg-gray-300 rounded w-2/3" />
        <div className="h-44 bg-gray-300 rounded mx-auto w-44" />
        <div className="space-y-4">
          {[...Array(4)].map((_, index) => (
            <div
              key={index}
              className="flex justify-between items-center border-b border-gray-200 pb-4"
            >
              <div className="flex items-center gap-3 w-1/3 h-5 bg-gray-300 rounded" />
              <div className="flex gap-2 w-1/2 h-4 bg-gray-300 rounded" />
            </div>
          ))}
        </div>
        <div className="h-12 bg-gray-300 rounded w-full" />
      </div>
    );
  }

  return (
    <div className="px-8 flex flex-col gap-8 h-full overflow-y-scroll pb-6">
      <div className="flex flex-col gap-1">
        <span className="font-semibold text-2xl">
          {t('asset.ready_title')}
        </span>
        <span className="text-xl">
          {t('asset.ready_text')}
        </span>
      </div>

      <div className="flex justify-center items-center">
        <img
          alt={selected.id}
          src={getImage(selected.files)}
          className={cn(
            'h-44',
            { 'grayscale opacity-50': !available.includes(selected.state) }
          )}
        />
      </div>

      <div className="h-full flex flex-col gap-3">
        {items.map((item, index) =>
          <div
            key={index}
            className={cn(
              'flex justify-between items-center',
              { 'border-b border-[#e7e7e7] pb-5': index !== items.length - 1 }
            )}
          >
            <div className="flex items-center gap-3">
              <Icon icon={item.icon} width="25" />
              <span className="font-semibold text-base">{item.title}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">{item.text}</span>
              <span className="text-[#808080] text-xs">({item.note})</span>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="terms-checkbox"
          checked={terms}
          onChange={() => setTerms(!terms)}
          className="h-5 w-5 focus:ouline-none border-gray-300 rounded"
        />
        <span
          htmlFor="terms-checkbox"
          className="text-xs text-dark"
          dangerouslySetInnerHTML={{ __html: t('terms.text') }}
          onClick={() => navigate('/terms')}
        />
      </div>

      <div>
        {available.includes(selected.state)
          ?
          <SwipeButton
            onComplete={handleRetrieve}
            text={t('locker.swipe_for_open')}
            completed={completed}
            loadingText={t('locker.opening_door')}
            completeText={t('locker.door_opened')}
            disabled={!terms}
          />
          : <Button
            onClick={null}
            color="secondary"
            size="lg"
            text={t('request.notify')}
          />}
      </div>
    </div>
  );
};

export default AssetPickup;
